import React, { useCallback, useMemo } from 'react'
import { FaPlay as PlayIcon } from 'react-icons/fa6'
import { useNavigate } from '@tanstack/react-router'
import {
  useSequencerStore,
  selectCurrentProject,
  selectCurrentBuses,
  selectCurrentContainer,
  type PadContent,
  type PadMidiRouting,
} from '@/stores/sequencerStore'
import type { Envelope } from '@busswerks/shared'
import { KITS, useAudioEngine, type VoiceType } from '@/hooks/useAudioEngine'
import { useSequencerTriggers } from '@/hooks/useSequencerTriggers'
import { useAllSamples, type Sample } from '@/hooks/useSampleUpload'
import {
  listInstrumentsByCategory,
  getInstrument,
} from '@/audio/plugins/registry'
import { ParamSchemaForm } from '@/components/werkshop/ParamSchemaForm'
import { usePluginEntitlementMap } from '@/hooks/usePluginEntitlement'
import { defaultNoteForPad, VOICE_TYPES, labelForVoice } from '@/lib/resolvePad'
import { GRANULAR_PAD_PRESETS, type GranularPadPresetId } from '@/lib/granular'

/**
 * In-tab per-pad settings form — the Cell Editor's Pad tab body
 * (see `StepInspectorPanel`). Successor to the deleted
 * `PadEditor.tsx` modal; every per-pad setting the modal owned
 * now lives here:
 *
 *   - Preview button (audition without focus change)
 *   - Content kind picker (synth / sample / granular / midi /
 *     instrument-plugin)
 *   - Kind-specific fields (voice / sample + slice / MIDI label +
 *     channel)
 *   - MIDI output routing
 *   - Mixer bus
 *   - Per-pad gain
 *   - Envelope ADSR (sample / synth / granular)
 *   - Granular grain-param panel (preset pills + 7 sliders + 2
 *     toggles)
 *   - Instrument plugin macros (rendered via `ParamSchemaForm`)
 *
 * Follow-up deferred: the per-pad recording flow (ARM + record-
 * to-pad) lives in the transport, and custom MIDI pads (overflow
 * above index 16) can still be mutated via store actions but have
 * no dedicated UI after the modal deletion — create + rename +
 * delete flows for those will land when we find a proper home
 * (likely the MIDI Router page or a small dedicated dialog).
 */
export const PadSettingsForm: React.FC<{ padIndex: number }> = ({
  padIndex,
}) => {
  const project = useSequencerStore(selectCurrentProject)
  const container = useSequencerStore(selectCurrentContainer)
  const buses = useSequencerStore(selectCurrentBuses)
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const setPadOutput = useSequencerStore(s => s.setPadOutput)
  const setPadBus = useSequencerStore(s => s.setPadBus)
  const { data: samples = [] } = useAllSamples()
  const entitlementFor = usePluginEntitlementMap()
  const navigate = useNavigate()

  const padDef = project?.padDefs?.[padIndex]
  const kit = project ? KITS[project.kitId] : undefined
  const kitDefault = kit?.voices[padIndex]
  const padOutput: PadMidiRouting | undefined = project?.padOutputs?.[padIndex]

  // Active bank's padBus map — only sits on the active bank's
  // banks[bankId].padBus entry, not the project. Falls back to
  // undefined when no bus is assigned.
  const activeBankId = container?.activeBankId ?? null
  const currentBusId =
    activeBankId && container?.banks[activeBankId]?.padBus?.[padIndex]

  const instrumentCategories = useMemo(() => listInstrumentsByCategory(), [])

  // Preview — audition the focused pad without changing the step
  // focus. Matches the Inspector's old Preview button but lives
  // next to the pad settings so the full per-pad editor is one
  // surface.
  const { resume, currentTime } = useAudioEngine()
  const { firePad } = useSequencerTriggers()
  const handlePreview = useCallback(() => {
    resume()
    firePad(padIndex, currentTime(), { focusOnClick: false })
  }, [resume, firePad, padIndex, currentTime])

  // Sample pads (legacy `kind: 'sample'`) and sampler-instrument
  // pads (`kind: 'instrument'` with `instrumentId: 'sampler'`)
  // can bind to a named slice on the underlying sample. Resolve
  // the current sample + its slice list so the slice picker
  // renders whenever the pad points at a sample with slices.
  const currentSampleId =
    padDef?.kind === 'sample'
      ? padDef.sampleId
      : padDef?.kind === 'instrument' && padDef.instrumentId === 'sampler'
        ? padDef.sampleId
        : undefined
  const currentSample = useMemo(
    () => samples.find(s => s.id === currentSampleId),
    [samples, currentSampleId],
  )
  const currentSliceId =
    padDef?.kind === 'sample' || padDef?.kind === 'instrument'
      ? padDef.sliceId
      : undefined

  // Resolve the registered plugin for instrument-kind pads so the
  // param-schema form renders the plugin's native macros
  // (cutoff, LFO rate, detune spread, etc.). Non-instrument pads
  // don't have a schema to render — the kind-specific fields above
  // cover them.
  const plugin = useMemo(() => {
    if (!padDef || padDef.kind !== 'instrument') return null
    return getInstrument(padDef.instrumentId) ?? null
  }, [padDef])
  const handleParamChange = (paramId: string, value: number) => {
    if (!padDef || padDef.kind !== 'instrument') return
    setPadContent(padIndex, {
      ...padDef,
      preset: {
        ...(padDef.preset ?? {}),
        [paramId]: value,
      },
    })
  }

  const currentPickerValue: string = padDef
    ? padDef.kind === 'instrument' && padDef.instrumentId
      ? `instrument:${padDef.instrumentId}`
      : padDef.kind
    : kitDefault
      ? 'kit-default'
      : 'empty'

  const handleKindChange = (next: string) => {
    if (next.startsWith('instrument:')) {
      const instrumentId = next.slice('instrument:'.length)
      if (!instrumentId) return
      if (entitlementFor(instrumentId) === null) {
        void navigate({ to: '/werkshop/store', hash: instrumentId })
        return
      }
      if (instrumentId === 'sampler') {
        const first = samples[0]
        if (!first) return
        setPadContent(padIndex, {
          kind: 'instrument',
          instrumentId: 'sampler',
          midiNote: kitDefault?.midiNote ?? defaultNoteForPad(padIndex),
          preset: {},
          gainDb: 0,
          sampleId: first.id,
          s3Key: first.s3Key,
        })
        return
      }
      const plugin = getInstrument(instrumentId)
      setPadContent(padIndex, {
        kind: 'instrument',
        instrumentId,
        label: plugin?.label,
        midiNote: kitDefault?.midiNote ?? defaultNoteForPad(padIndex),
        preset: {},
        gainDb: 0,
      })
      return
    }
    if (next === 'kit-default' || next === 'empty') {
      setPadContent(padIndex, null)
      return
    }
    if (next === 'synth') {
      const voice: VoiceType = kitDefault?.type ?? 'kick'
      setPadContent(padIndex, {
        kind: 'synth',
        voice,
        midiNote: kitDefault?.midiNote ?? defaultNoteForPad(padIndex),
      })
      return
    }
    if (next === 'sample') {
      const first = samples[0]
      if (!first) return
      setPadContent(padIndex, {
        kind: 'sample',
        sampleId: first.id,
        s3Key: first.s3Key,
        name: first.name,
        midiNote: defaultNoteForPad(padIndex),
      })
      return
    }
    if (next === 'granular') {
      const first = samples[0]
      if (!first) return
      const seed = GRANULAR_PAD_PRESETS.freeze
      setPadContent(padIndex, {
        kind: 'granular',
        sampleId: first.id,
        s3Key: first.s3Key,
        name: first.name,
        midiNote: defaultNoteForPad(padIndex),
        preset: 'freeze',
        grainSizeMs: seed.grainSizeMs,
        overlap: seed.overlap,
        detuneCents: seed.detuneCents,
        playbackRate: seed.playbackRate,
        reverse: seed.reverse,
        positionSec: 0,
        positionSpread: seed.positionSpread,
        pitchSpread: seed.pitchSpread,
        loop: seed.loop,
      })
      return
    }
    if (next === 'midi') {
      setPadContent(padIndex, {
        kind: 'midi',
        label: `MIDI ${padIndex + 1}`,
        midiNote: defaultNoteForPad(padIndex),
        channel: 1,
      })
      return
    }
  }

  return (
    <div className="flex flex-col gap-3 text-xs">
      {/* Preview header — audition the focused pad without firing
          a step or shifting focus. Moved here from the Inspector
          so per-pad editing + audition live in one surface. */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-content-tertiary flex-1">
          Pad {padIndex + 1}
        </span>
        <button
          type="button"
          onClick={handlePreview}
          title={`Preview pad ${padIndex + 1} (no step recording, no focus change)`}
          aria-label={`Preview pad ${padIndex + 1}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-brd-subtle text-[10px] font-mono uppercase tracking-[0.14em] text-content-secondary hover:border-accent hover:text-accent transition-colors"
        >
          <PlayIcon size={9} />
          Preview
        </button>
      </div>

      {/* Kind picker */}
      <Row label="Kind">
        <select
          value={currentPickerValue}
          onChange={e => handleKindChange(e.target.value)}
          className="flex-1 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
        >
          <optgroup label="Built-in">
            {kitDefault && <option value="kit-default">Kit default</option>}
            {!kitDefault && <option value="empty">Empty</option>}
            <option value="synth">Synth voice</option>
            <option value="sample">Sample</option>
            <option value="granular">Granular</option>
            <option value="midi">External MIDI</option>
          </optgroup>
          {(['synth', 'sampler', 'hybrid'] as const).map(category => {
            const plugins = instrumentCategories[category]
            if (!plugins?.length) return null
            return (
              <optgroup key={category} label={`Plugins · ${category}`}>
                {plugins.map(p => {
                  const locked = entitlementFor(p.id) === null
                  return (
                    <option key={p.id} value={`instrument:${p.id}`}>
                      {p.label}
                      {locked ? ' 🔒' : ''}
                    </option>
                  )
                })}
              </optgroup>
            )
          })}
        </select>
      </Row>

      {/* Kind-specific fields */}
      {padDef?.kind === 'synth' && (
        <Row label="Voice">
          <select
            value={padDef.voice}
            onChange={e =>
              setPadContent(padIndex, {
                ...padDef,
                voice: e.target.value as VoiceType,
              })
            }
            className="flex-1 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
          >
            {VOICE_TYPES.map(v => (
              <option key={v} value={v}>
                {labelForVoice(v)}
              </option>
            ))}
          </select>
        </Row>
      )}

      {(padDef?.kind === 'sample' ||
        (padDef?.kind === 'instrument' &&
          padDef.instrumentId === 'sampler')) && (
        <>
          <SamplePicker
            value={padDef.sampleId}
            samples={samples}
            onChange={s => {
              if (padDef.kind === 'sample') {
                setPadContent(padIndex, {
                  ...padDef,
                  sampleId: s.id,
                  s3Key: s.s3Key,
                  name: s.name,
                  sliceId: undefined,
                })
              } else {
                setPadContent(padIndex, {
                  ...padDef,
                  sampleId: s.id,
                  s3Key: s.s3Key,
                  sliceId: undefined,
                })
              }
            }}
          />
          {currentSample?.slices && currentSample.slices.length > 0 && (
            <Row label="Slice">
              <select
                value={currentSliceId ?? '__full__'}
                onChange={e => {
                  const v = e.target.value
                  const nextSliceId = v === '__full__' ? undefined : v
                  if (padDef.kind === 'sample') {
                    setPadContent(padIndex, {
                      ...padDef,
                      sliceId: nextSliceId,
                    })
                  } else {
                    setPadContent(padIndex, {
                      ...padDef,
                      sliceId: nextSliceId,
                    })
                  }
                }}
                className="flex-1 min-w-0 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary font-mono tabular-nums"
                title="Play only this slice from the sample. Set to Full sample to play start-to-end."
              >
                <option value="__full__">Full sample</option>
                {currentSample.slices.map((sl, i) => (
                  // Positional "Slice N" labels — the slice's underlying
                  // `id` (e.g. "slice-1776662004274-lhws3fjzb") is
                  // auto-generated and not user-visible anywhere else,
                  // so `sl.label ?? sl.id` used to dump an unreadable
                  // hash into the option. Use 1-indexed position so it
                  // matches how users count slices in the slicer UI.
                  <option key={sl.id} value={sl.id}>
                    {sl.label ?? `Slice ${i + 1}`} · {sl.startSec.toFixed(2)}s →{' '}
                    {sl.endSec.toFixed(2)}s
                  </option>
                ))}
              </select>
            </Row>
          )}
        </>
      )}

      {padDef?.kind === 'midi' && (
        <>
          <Row label="Label">
            <input
              type="text"
              value={padDef.label}
              onChange={e =>
                setPadContent(padIndex, { ...padDef, label: e.target.value })
              }
              className="flex-1 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
            />
          </Row>
          <Row label="Channel">
            <input
              type="number"
              min={1}
              max={16}
              step={1}
              value={padDef.channel}
              onChange={e => {
                const ch = Math.max(
                  1,
                  Math.min(16, Math.round(Number(e.target.value) || 1)),
                )
                setPadContent(padIndex, { ...padDef, channel: ch })
              }}
              className="w-20 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary font-mono tabular-nums"
            />
          </Row>
        </>
      )}

      {/* MIDI out routing */}
      <Row label="MIDI out">
        <select
          value={padOutput ?? '__default__'}
          onChange={e => {
            const v = e.target.value
            setPadOutput(padIndex, v === '__default__' ? null : v)
          }}
          className="flex-1 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
          title="Default = broadcast to every routed output; Off = no MIDI. Specific devices are configurable in the MIDI Router."
        >
          <option value="__default__">Default (broadcast)</option>
          <option value="off">Off</option>
          <option value="all">All</option>
        </select>
      </Row>

      {/* Bus routing */}
      {buses.length > 0 && (
        <Row label="Bus">
          <select
            value={currentBusId ?? '__master__'}
            onChange={e => {
              const v = e.target.value
              setPadBus(padIndex, v === '__master__' ? '' : v)
            }}
            className="flex-1 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
          >
            <option value="__master__">Master</option>
            {buses.map(b => (
              <option key={b.id} value={b.id}>
                {b.name ?? b.id}
              </option>
            ))}
          </select>
        </Row>
      )}

      {/* Per-pad gain. Only applicable when the pad has a content
          variant that reads the `gainDb` field. */}
      {padDef &&
        (padDef.kind === 'sample' ||
          padDef.kind === 'granular' ||
          padDef.kind === 'instrument') && (
          <Row label="Gain">
            <input
              type="range"
              min={-24}
              max={24}
              step={0.5}
              value={padDef.gainDb ?? 0}
              onChange={e =>
                setPadContent(padIndex, {
                  ...padDef,
                  gainDb: Number(e.target.value),
                })
              }
              className="flex-1 accent-[color:var(--accent)]"
            />
            <span className="w-12 text-right font-mono text-[10px] text-content-secondary tabular-nums">
              {(padDef.gainDb ?? 0) > 0 ? '+' : ''}
              {(padDef.gainDb ?? 0).toFixed(1)} dB
            </span>
          </Row>
        )}

      {/* Playback mode — only sample pads. `one-shot` (the default)
          ignores cell.length for audio gating so each slice rings
          out naturally; `gated` honours cell.length like a
          traditional drum-machine trigger. Lives here rather than
          the Cell tab because it's a per-pad semantic, not a
          per-step lock. */}
      {padDef?.kind === 'sample' && (
        <Row label="Playback">
          <div
            className="flex-1 grid grid-cols-2 gap-1 rounded border border-brd-subtle p-0.5"
            role="radiogroup"
            aria-label="Sample playback mode"
          >
            {(['one-shot', 'gated'] as const).map(mode => {
              const active = (padDef.playback ?? 'one-shot') === mode
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`text-[10px] uppercase tracking-[0.1em] font-mono py-1 rounded ${
                    active
                      ? 'bg-[color:var(--accent)] text-black'
                      : 'text-content-secondary hover:bg-brd-subtle'
                  }`}
                  onClick={() =>
                    setPadContent(padIndex, { ...padDef, playback: mode })
                  }
                >
                  {mode === 'one-shot' ? 'One-Shot' : 'Gated'}
                </button>
              )
            })}
          </div>
        </Row>
      )}

      {/* Pad-default envelope — applies to sample / synth / granular
          pads as the baseline ADSR. Per-step envelope locks in the
          Cell tab override this at trigger time. Not applicable to
          instrument-kind pads (those manage their own envelope via
          the macros form below) or to midi-kind (no audio). */}
      {padDef &&
        (padDef.kind === 'sample' ||
          padDef.kind === 'synth' ||
          padDef.kind === 'granular') && (
          <PadEnvelopeEditor
            envelope={padDef.envelope}
            onChange={next => {
              // Same type-narrowing dance the modal did: each kind
              // reconstructs the whole discriminated union with the
              // new envelope, preserving every other field.
              if (padDef.kind === 'sample') {
                setPadContent(padIndex, { ...padDef, envelope: next })
              } else if (padDef.kind === 'synth') {
                setPadContent(padIndex, { ...padDef, envelope: next })
              } else {
                setPadContent(padIndex, { ...padDef, envelope: next })
              }
            }}
          />
        )}

      {/* Granular grain-param panel — collapsible, only for
          kind: 'granular' pads. Covers grain size / overlap /
          detune / rate / position / spreads / reverse / loop with
          the same four preset pills the modal offered (Freeze /
          Stretch / Cloud / Reverse Swell). */}
      {padDef?.kind === 'granular' && (
        <GranularEditor
          padDef={padDef}
          onChange={next => setPadContent(padIndex, next)}
          sampleDurationSec={
            samples.find(s => s.id === padDef.sampleId)?.durationSec ?? 0
          }
        />
      )}

      {/* Instrument macros — the top-level knobs exposed by the
          registered plugin (cutoff, LFO rate, detune spread, etc.).
          Lives here rather than the bottom Inspector so per-pad
          editing stays in a single surface. */}
      {plugin && padDef?.kind === 'instrument' && (
        <div className="pt-1 border-t border-brd-subtle">
          <ParamSchemaForm
            schema={plugin.paramSchema}
            values={padDef.preset ?? {}}
            onChange={handleParamChange}
            heading={`${plugin.label} · macros`}
            compact
          />
        </div>
      )}

      <p className="text-[10px] text-content-tertiary leading-relaxed pt-1 border-t border-brd-subtle">
        Envelope + granular grain params still live in ⚙ Advanced — open from
        the header button or long-press the pad.
      </p>
    </div>
  )
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  // min-w-0 on the flex child container is what lets `flex-1` selects
  // honour the panel width — without it, long <option> values (sample
  // names, slice ids) blow the row past its parent and the whole form
  // overflows. See PR #211 for the bug this fixes.
  <div className="flex items-center gap-2 min-w-0">
    <span className="w-20 text-[10px] font-mono uppercase tracking-[0.12em] text-content-tertiary shrink-0">
      {label}
    </span>
    <div className="flex-1 min-w-0 flex items-center gap-2">{children}</div>
  </div>
)

const SamplePicker: React.FC<{
  value: string | undefined
  samples: Sample[]
  onChange: (s: Sample) => void
}> = ({ value, samples, onChange }) => {
  if (samples.length === 0) {
    return (
      <Row label="Sample">
        <span className="text-[10px] text-content-tertiary italic">
          No samples uploaded yet.
        </span>
      </Row>
    )
  }
  return (
    <Row label="Sample">
      <select
        value={value ?? ''}
        onChange={e => {
          const s = samples.find(x => x.id === e.target.value)
          if (s) onChange(s)
        }}
        className="flex-1 min-w-0 bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary"
      >
        {samples.map(s => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </Row>
  )
}

/**
 * Per-pad default envelope (ADSR). Writes `padDef.envelope` when
 * any field is set; clears the field entirely when the user resets
 * every slider so the serialised pad stays lean. Per-step envelope
 * locks (Cell tab) take precedence at trigger time.
 */
const ENVELOPE_SLIDERS: ReadonlyArray<{
  key: keyof Envelope
  label: string
  unit: 's' | ''
  min: number
  max: number
  fallback: number
}> = [
  { key: 'a', label: 'A', unit: 's', min: 0, max: 2, fallback: 0.001 },
  { key: 'd', label: 'D', unit: 's', min: 0, max: 2, fallback: 0 },
  { key: 's', label: 'S', unit: '', min: 0, max: 1, fallback: 1 },
  { key: 'r', label: 'R', unit: 's', min: 0, max: 2, fallback: 0.02 },
]

/**
 * Granular grain-param panel for `kind: 'granular'` pads. Matches
 * the modal's layout 1:1 so users who learned the knobs there
 * don't have to re-learn anything — same four presets, same eight
 * sliders + two toggles. Writes via `setPadContent` (threaded
 * through `onChange`) so every pattern in the bank picks up the
 * change.
 *
 * Collapsed by default to keep the Pad tab scannable; the caret
 * reminds the user there's more here without forcing it.
 */
const GranularEditor: React.FC<{
  padDef: Extract<PadContent, { kind: 'granular' }>
  onChange: (next: PadContent) => void
  sampleDurationSec: number
}> = ({ padDef, onChange, sampleDurationSec }) => {
  const update = <K extends keyof typeof padDef>(
    patch: Partial<Pick<typeof padDef, K>>,
  ) => {
    onChange({ ...padDef, ...patch, preset: undefined })
  }
  const applyPreset = (id: GranularPadPresetId) => {
    const seed = GRANULAR_PAD_PRESETS[id]
    onChange({
      ...padDef,
      preset: id,
      grainSizeMs: seed.grainSizeMs,
      overlap: seed.overlap,
      detuneCents: seed.detuneCents,
      playbackRate: seed.playbackRate,
      reverse: seed.reverse,
      loop: seed.loop,
      positionSpread: seed.positionSpread,
      pitchSpread: seed.pitchSpread,
    })
  }
  return (
    <details className="pt-1 border-t border-brd-subtle">
      <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary hover:text-content-secondary select-none list-none flex items-center gap-2">
        <span>▸</span>
        Granular grain
        {padDef.preset && (
          <span className="text-accent normal-case tracking-normal">
            · {padDef.preset}
          </span>
        )}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1">
          {(
            [
              ['freeze', 'Freeze'],
              ['stretch', 'Stretch'],
              ['cloud', 'Cloud'],
              ['reverse-swell', 'Reverse Swell'],
            ] as const
          ).map(([id, label]) => {
            const active = padDef.preset === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => applyPreset(id)}
                className={`text-[10px] font-mono px-2 py-0.5 rounded border transition-colors ${
                  active
                    ? 'border-accent text-accent bg-accent/10'
                    : 'border-brd-subtle text-content-secondary hover:text-content-primary'
                }`}
                title={`Seed grain params from the ${label} preset`}
              >
                {label}
              </button>
            )
          })}
        </div>
        <div className="grid grid-cols-1 gap-1.5">
          <GranularSlider
            label="Grain"
            unit="ms"
            min={10}
            max={1000}
            step={5}
            value={padDef.grainSizeMs}
            onChange={v => update({ grainSizeMs: v })}
          />
          <GranularSlider
            label="Overlap"
            unit=""
            min={0}
            max={1}
            step={0.01}
            value={padDef.overlap}
            format={v => `${Math.round(v * 100)}%`}
            onChange={v => update({ overlap: v })}
          />
          <GranularSlider
            label="Detune"
            unit="¢"
            min={-2400}
            max={2400}
            step={1}
            value={padDef.detuneCents}
            onChange={v => update({ detuneCents: v })}
          />
          <GranularSlider
            label="Rate"
            unit="×"
            min={0.25}
            max={4}
            step={0.01}
            value={padDef.playbackRate}
            format={v => v.toFixed(2)}
            onChange={v => update({ playbackRate: v })}
          />
          <GranularSlider
            label="Pos"
            unit="s"
            min={0}
            max={Math.max(0.01, sampleDurationSec)}
            step={0.01}
            value={Math.min(padDef.positionSec, Math.max(0, sampleDurationSec))}
            format={v => v.toFixed(2)}
            onChange={v => update({ positionSec: v })}
          />
          <GranularSlider
            label="PosSprd"
            unit=""
            min={0}
            max={1}
            step={0.01}
            value={padDef.positionSpread}
            format={v => `${Math.round(v * 100)}%`}
            onChange={v => update({ positionSpread: v })}
          />
          <GranularSlider
            label="PitchSprd"
            unit="¢"
            min={0}
            max={2400}
            step={1}
            value={padDef.pitchSpread}
            onChange={v => update({ pitchSpread: v })}
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-content-secondary">
            <input
              type="checkbox"
              checked={padDef.reverse}
              onChange={e => update({ reverse: e.target.checked })}
              className="accent-[color:var(--accent)]"
            />
            Reverse
          </label>
          <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-content-secondary">
            <input
              type="checkbox"
              checked={padDef.loop}
              onChange={e => update({ loop: e.target.checked })}
              className="accent-[color:var(--accent)]"
            />
            Loop
          </label>
        </div>
      </div>
    </details>
  )
}

const GranularSlider: React.FC<{
  label: string
  unit: string
  min: number
  max: number
  step: number
  value: number
  onChange: (v: number) => void
  format?: (v: number) => string
}> = ({ label, unit, min, max, step, value, onChange, format }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 text-[9px] font-mono uppercase tracking-wider text-content-tertiary">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="flex-1 accent-[color:var(--accent)]"
      />
      <span className="w-12 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
        {format ? format(value) : Math.round(value)}
        {unit}
      </span>
    </div>
  )
}

const PadEnvelopeEditor: React.FC<{
  envelope: Partial<Envelope> | undefined
  onChange: (next: Partial<Envelope> | undefined) => void
}> = ({ envelope, onChange }) => {
  const env = envelope ?? {}
  const update = (patch: Partial<Envelope>) => {
    const merged: Partial<Envelope> = { ...env, ...patch }
    const cleaned: Partial<Envelope> = {}
    for (const k of ['a', 'd', 's', 'r'] as const) {
      if (merged[k] !== undefined) cleaned[k] = merged[k]
    }
    onChange(Object.keys(cleaned).length > 0 ? cleaned : undefined)
  }
  const hasAny = Object.keys(env).length > 0
  return (
    <details className="pt-1 border-t border-brd-subtle">
      <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary hover:text-content-secondary select-none list-none flex items-center gap-2">
        <span className="group-open:hidden">▸</span>
        Envelope (pad default)
        {hasAny && <span className="text-accent">●</span>}
      </summary>
      <div className="mt-2 flex flex-col gap-1.5">
        {ENVELOPE_SLIDERS.map(cfg => {
          const value = env[cfg.key] ?? cfg.fallback
          return (
            <div key={cfg.key} className="flex items-center gap-2">
              <span className="w-4 text-[10px] font-mono text-content-secondary">
                {cfg.label}
              </span>
              <input
                type="range"
                min={cfg.min}
                max={cfg.max}
                step={0.005}
                value={value}
                onChange={e => update({ [cfg.key]: Number(e.target.value) })}
                className="flex-1 accent-[color:var(--accent)]"
              />
              <span className="w-12 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
                {cfg.unit === 's'
                  ? `${(value * 1000).toFixed(0)}ms`
                  : value.toFixed(2)}
              </span>
              <button
                type="button"
                onClick={() => update({ [cfg.key]: undefined })}
                disabled={env[cfg.key] === undefined}
                className="text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-30"
                title="Reset to engine default"
              >
                ⎌
              </button>
            </div>
          )
        })}
      </div>
    </details>
  )
}
